import { ColumnDef } from '@tanstack/react-table';
import { Eye, MoreHorizontal, Pencil, Play, Trash2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import TaskStatusBadge from '../components/TaskStatusBadge';
import { ConfirmDialog, MessageDialog } from '../components/ui/alert-dialog-helper';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { createSortableColumn } from '../components/ui/column-helpers';
import { DataTable } from '../components/ui/data-table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { Task, TaskStatus } from '../types/task';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  roles: Array<{ key: string; name: string; description: string }>;
}

interface TaskStats {
  total: number;
  pending: number;
  assigned: number;
  in_progress: number;
  completed: number;
  blocked: number;
  my_tasks: number;
}

const Tasks: React.FC = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [stats, setStats] = useState<TaskStats>({
    total: 0,
    pending: 0,
    assigned: 0,
    in_progress: 0,
    completed: 0,
    blocked: 0,
    my_tasks: 0,
  });
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'my-tasks'>('my-tasks');

  // Server-side pagination and sorting state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, pages: 0 });
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);

  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<Task | null>(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
    }
  }, []);

  useEffect(() => {
    if (user) {
      loadStats();
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadTasks(user.id);
    }
  }, [filter, page, limit, sortBy, sortOrder, user, debouncedSearch]);

  const loadTasks = async (userId: string) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      let url;
      if (filter === 'my-tasks') {
        url = `/api/tasks/user/${userId}?active_only=false`;
      } else {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
          sortBy,
          sortOrder,
        });
        if (debouncedSearch) params.set('search', debouncedSearch);
        url = `/api/tasks?${params}`;
      }

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        // getAllTasks returns { data, pagination }, getTasksByUser returns data array
        if (filter === 'my-tasks') {
          const tasksData = Array.isArray(data.data) ? data.data : [];
          setTasks(tasksData);
        } else {
          setTasks(data.data || []);
          setPagination(data.pagination || { total: 0, page: 1, limit: 10, pages: 0 });
        }
      }
    } catch (err) {
      console.error('Error loading tasks:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePaginationChange = (newPage: number, newLimit: number) => {
    setPage(newPage);
    setLimit(newLimit);
  };

  const handleSortingChange = (newSortBy: string, newSortOrder: 'ASC' | 'DESC') => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    setPage(1);
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const userId = user?.id;
      const url = userId ? `/api/tasks/stats?user_id=${userId}` : '/api/tasks/stats';
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (err) {
      console.error('Error loading task stats:', err);
    }
  };

  const handleStartTask = async (taskId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/tasks/${taskId}/start`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success && user) {
        loadTasks(user.id);
        loadStats();
      }
    } catch (err) {
      console.error('Error starting task:', err);
    }
  };

  const handleDeleteTask = async () => {
    if (!taskToDelete) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/tasks/${taskToDelete.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();

      if (data.success) {
        loadTasks(user?.id || ''); // Reload tasks for the current user
        loadStats();
      } else {
        setErrorMessage(data.message || 'Failed to delete task');
        setErrorDialogOpen(true);
      }
    } catch (err) {
      console.error('Error deleting task:', err);
      setErrorMessage('An error occurred while deleting the task');
      setErrorDialogOpen(true);
    } finally {
      setTaskToDelete(null);
    }
  };

  const formatTaskType = (type: string) => {
    return type
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getPriorityBadge = (priority: string) => {
    const priorityMap: Record<string, 'default' | 'warning' | 'destructive' | 'secondary'> = {
      urgent: 'destructive',
      high: 'warning',
      normal: 'default',
      low: 'secondary',
    };
    return priorityMap[priority] || 'default';
  };

  const columns: ColumnDef<Task>[] = [
    {
      ...createSortableColumn<Task>('task_number', 'Task Number'),
      cell: ({ row }) => (
        <button
          onClick={() => navigate(`/tasks/${row.original.id}`)}
          className="font-medium text-gray-900 hover:text-gray-700 underline hover:no-underline"
        >
          {row.original.task_number}
        </button>
      ),
    },
    {
      accessorKey: 'task_type',
      header: 'Type',
      enableSorting: false,
      cell: ({ row }) => formatTaskType(row.original.task_type),
    },
    {
      accessorKey: 'job.job_number',
      header: 'Job',
      enableSorting: false,
      cell: ({ row }) => {
        const job = row.original.job;
        return job ? (
          <Button
            variant="link"
            size="sm"
            className="p-0 h-auto font-mono"
            onClick={() => navigate(`/jobs/${job.id}`)}
          >
            {job.job_number}
          </Button>
        ) : (
          '-'
        );
      },
    },
    {
      accessorKey: 'assigned_to_user',
      header: 'Assigned To',
      enableSorting: false,
      cell: ({ row }) => {
        const assignedUser = row.original.assigned_to_user;
        return assignedUser
          ? `${assignedUser.first_name} ${assignedUser.last_name}`
          : <span className="text-muted-foreground italic">Unassigned</span>;
      },
    },
    {
      ...createSortableColumn<Task>('status', 'Status'),
      cell: ({ row }) => <TaskStatusBadge status={row.original.status} />,
    },
    {
      ...createSortableColumn<Task>('priority', 'Priority'),
      cell: ({ row }) => (
        <Badge variant={getPriorityBadge(row.original.priority)}>
          {row.original.priority.toUpperCase()}
        </Badge>
      ),
    },
    {
      ...createSortableColumn<Task>('planned_start_at', 'Planned Start'),
      cell: ({ row }) => {
        const value = row.getValue('planned_start_at') as string;
        if (!value) return <span className="text-muted-foreground">-</span>;
        const date = new Date(value);
        if (isNaN(date.getTime())) return <span className="text-muted-foreground">-</span>;
        return date.toLocaleDateString();
      },
    },
    {
      ...createSortableColumn<Task>('created_at', 'Created At'),
      cell: ({ row }) => {
        const value = row.getValue('created_at') as string;
        if (!value) return <span className="text-muted-foreground">-</span>;
        const date = new Date(value);
        if (isNaN(date.getTime())) return <span className="text-muted-foreground">-</span>;
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}`;
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const task = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate(`/tasks/${task.id}`)}>
                <Eye className="h-4 w-4 mr-2" />
                View
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`/tasks/${task.id}/edit`)}>
                <Pencil className="h-4 w-4 mr-2" />
                Edit
              </DropdownMenuItem>
              {task.status === TaskStatus.ASSIGNED && (
                <DropdownMenuItem onClick={() => handleStartTask(task.id)}>
                  <Play className="h-4 w-4 mr-2" />
                  Start
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                className="text-red-600"
                onClick={() => {
                  setTaskToDelete(task);
                  setDeleteDialogOpen(true);
                }}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} currentPage="tasks" />
      <main className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Tasks</h1>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div
            onClick={() => setFilter('all')}
            className={`p-6 rounded-lg shadow-sm cursor-pointer transition-all border border-transparent hover:border-primary ${
              filter === 'all' ? 'bg-primary/5' : 'bg-card'
            }`}
          >
            <div className="text-2xl font-bold text-primary">{stats.total}</div>
            <div className="text-sm text-muted-foreground">Total Tasks</div>
          </div>
          <div
            onClick={() => setFilter('my-tasks')}
            className={`p-6 rounded-lg shadow-sm cursor-pointer transition-all border border-transparent hover:border-primary ${
              filter === 'my-tasks' ? 'bg-primary/5' : 'bg-card'
            }`}
          >
            <div className="text-2xl font-bold text-blue-600">{stats.my_tasks ?? 0}</div>
            <div className="text-sm text-muted-foreground">My Tasks</div>
          </div>
          <div className="bg-card p-6 rounded-lg shadow-sm">
            <div className="text-2xl font-bold text-yellow-600">{stats.in_progress}</div>
            <div className="text-sm text-muted-foreground">In Progress</div>
          </div>
          <div className="bg-card p-6 rounded-lg shadow-sm">
            <div className="text-2xl font-bold text-green-600">{stats.completed}</div>
            <div className="text-sm text-muted-foreground">Completed</div>
          </div>
        </div>

        {/* Tasks Table */}
        <DataTable
          columns={columns}
          data={tasks}
          loading={loading}
          searchKey="task_number"
          searchPlaceholder="Search tasks..."
          searchValue={search}
          onSearchChange={(v) => { setSearch(v); setPage(1); }}
          emptyMessage="No tasks found"
          serverSide={filter === 'all'}
          pagination={filter === 'all' ? pagination : undefined}
          onPaginationChange={filter === 'all' ? handlePaginationChange : undefined}
          onSortingChange={filter === 'all' ? handleSortingChange : undefined}
        />
      </main>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Task"
        description={`Are you sure you want to delete task ${taskToDelete?.task_number}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteTask}
        variant="destructive"
      />

      {/* Error Message Dialog */}
      <MessageDialog
        open={errorDialogOpen}
        onOpenChange={setErrorDialogOpen}
        title="Error"
        description={errorMessage}
        variant="destructive"
      />
    </div>
  );
};

export default Tasks;

